import { useContext, useEffect } from 'react';
import { StoreContext } from '../context';
import { MeetCapabilitiesStore } from '../stores/meetCapabilitiesStore';

/**
 * The Meet capabilities store, loaded on first use.
 *
 * The load lives in the hook rather than at app start because the request needs a session, which
 * app start does not have. load() is idempotent, so several components mounting at once still
 * make one call.
 *
 * @param enabled whether to fetch on mount. Two reasons to pass false:
 *
 *        - **Meet cannot appear here.** navigation is rendered by every portal, so pass
 *          `isElectronApp()` — asking what someone may do in a feature that cannot appear on the
 *          page is a request per page load for an answer nobody reads.
 *        - **The caller will load it itself, at a better moment.** The Meet window mounts before
 *          it has read the portal host out of the Electron store and run checkAuthentiation, so
 *          a fetch on mount goes out with no token and returns 401. callApp passes false and
 *          calls load() from checkAuthentiation's success callback, where the session is a fact
 *          rather than something to wait for.
 *
 *        The store is returned either way, so callers need no second code path — its getters
 *        simply stay closed until something loads it.
 */
export const useMeetCapabilities = (enabled = true): MeetCapabilitiesStore => {
  const { meetCapabilitiesStore } = useContext<{
    meetCapabilitiesStore: MeetCapabilitiesStore;
  }>(StoreContext);

  useEffect(() => {
    if (!enabled) return;
    meetCapabilitiesStore?.load();
  }, [enabled, meetCapabilitiesStore]);

  return meetCapabilitiesStore;
};
